import { cn } from "@/lib/utils";
import { useHandoffPhase } from "@/contexts/HandoffPhaseContext";

type BrandLogoProps = {
  /** Tailwind size classes (e.g. "w-20 h-20") */
  sizeClassName: string;
  className?: string;
  /** Tailwind classes applied to the <img> (e.g. "animate-pulse") */
  imgClassName?: string;
  /**
   * When true, reserves layout space even when suppressed.
   * This helps prevent layout shifts on the first reveal.
   */
  reserveSpace?: boolean;
  /**
   * Optional tracer metadata.
   * Helps FlashTracer correlate DOM nodes to source files.
   */
  tracerId?: string;
  tracerFile?: string;
};

const BUTTERFLY_LOGO_WEBP = "/butterfly-logo-small-opaque.webp";
const BUTTERFLY_LOGO_PNG = "/butterfly-logo-small-opaque.png";

/**
 * BrandLogo
 * Hard-suppressed until handoff is complete to prevent "small butterfly" flashes.
 */
export const BrandLogo = ({
  sizeClassName,
  className,
  imgClassName,
  reserveSpace = true,
  tracerId,
  tracerFile,
}: BrandLogoProps) => {
  const { phase } = useHandoffPhase();

  if (phase !== "complete") {
    // HARD BLOCK: no logo may render until handoff is complete.
    // (We ignore reserveSpace here intentionally to guarantee zero-frame flashes.)
    return null;
  }

  return (
    <div
      className={cn("relative", sizeClassName, className)}
      data-flash-tracer={tracerId}
      data-flash-file={tracerFile}
      data-brand-logo="butterfly"
    >
      {/* Extra padding wrapper to prevent WebKit clipping during flutter animation */}
      <div className="absolute inset-[-8px] overflow-visible pointer-events-none" aria-hidden="true" />
      {/* CSS-independent solid background to prevent transparency checkerboard on first PWA paint */}
      <div
        className="absolute inset-0 rounded-full"
        style={{ backgroundColor: "hsl(var(--background, 0 0% 100%))" }}
        aria-hidden="true"
      />
      <picture>
        <source srcSet={BUTTERFLY_LOGO_WEBP} type="image/webp" />
        <img
          src={BUTTERFLY_LOGO_PNG}
          alt="LupusLogix butterfly logo"
          width={80}
          height={80}
          className={cn(
            "w-full h-full object-contain relative z-10",
            imgClassName ?? "animate-butterfly-flutter"
          )}
          style={{ willChange: 'transform' }}
          loading="eager"
          fetchPriority="high"
        />
      </picture>
    </div>
  );
};
